import { g0ToUnicode } from './charset.ts';
import { COLS, isPageNumber, type Fastext, type Link, type Row, type Subpage } from './types.ts';

/** Plain text of a row with attributes rendered as spaces (ASCII, not G0 glyphs). */
export function rowPlain(row: Row): string {
  let s = '';
  for (let x = 0; x < COLS; x++) {
    const c = (row[x] ?? 0x20) & 0x7f;
    s += c < 0x20 ? ' ' : String.fromCharCode(c);
  }
  return s;
}

/** Row text using the G0 English glyphs (for screen readers and captions). */
export function rowText(row: Row): string {
  let s = '';
  for (let x = 0; x < COLS; x++) s += g0ToUnicode(row[x] ?? 0x20);
  return s;
}

/**
 * Three digits 100-899 that are not part of a longer number (digits either
 * side), a decimal or thousands group (`12.5`, `1,400`, `19.30`), a price
 * (`#300` is £300 in G0, `$300`), a percentage or a quoted year (`'800`).
 * A run of dot leaders (`Rail plan......102`) is fine: only `digit.` counts
 * as a decimal point.
 */
const THREE_DIGIT = /(?<![\d#$'])(?<!\d[.,])([1-8]\d\d)(?![\d%'])(?![.,]\d)/g;

/**
 * Fallback scan for page numbers written in the text (brief 4.3) so that
 * times, scores, prices and years are not mistaken for links.
 */
export function scanLinks(rows: Row[], firstRow = 1, lastRow = 24): Link[] {
  const links: Link[] = [];
  for (let r = firstRow; r <= lastRow && r < rows.length; r++) {
    const s = rowPlain(rows[r]!);
    THREE_DIGIT.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = THREE_DIGIT.exec(s))) {
      const n = Number(m[1]);
      if (!isPageNumber(n)) continue;
      links.push({ row: r, colStart: m.index, colEnd: m.index + 3, target: { page: n }, origin: 'scan' });
    }
  }
  return links;
}

/**
 * Hit targets on row 24: each run of text introduced by an alpha red /
 * green / yellow / blue code maps to that Fastext key. If the row carries
 * no colour codes, fall back to four ten-column segments.
 */
export function fastextLinks(row24: Row, fastext: Fastext): Link[] {
  const keys: Array<keyof Fastext> = ['red', 'green', 'yellow', 'blue'];
  const links: Link[] = [];
  let current: { key: keyof Fastext; start: number } | null = null;
  let sawColour = false;
  const flush = (end: number) => {
    if (!current) return;
    const target = fastext[current.key];
    if (target !== null && end > current.start) {
      links.push({ row: 24, colStart: current.start, colEnd: end, target: { page: target }, origin: 'fastext' });
    }
    current = null;
  };
  for (let x = 0; x < COLS; x++) {
    const c = (row24[x] ?? 0x20) & 0x7f;
    if (c < 0x20) {
      if (c >= 0x01 && c <= 0x04) {
        flush(x);
        sawColour = true;
        current = { key: keys[c - 1]!, start: x };
      } else if (c <= 0x07 || (c >= 0x10 && c <= 0x17)) {
        flush(x);
      }
    }
  }
  flush(COLS);
  if (!sawColour) {
    keys.forEach((key, i) => {
      const target = fastext[key];
      if (target !== null) links.push({ row: 24, colStart: i * 10, colEnd: i * 10 + 10, target: { page: target }, origin: 'fastext' });
    });
  }
  // Trim trailing spaces from each label so hover doesn't light up padding.
  return links.map((l) => {
    let end = l.colEnd;
    while (end > l.colStart + 1 && ((row24[end - 1] ?? 0x20) & 0x7f) === 0x20) end--;
    return { ...l, colEnd: end };
  });
}

/**
 * Full link map for a subpage: explicit links first, then scanned
 * numbers that don't overlap them, then the Fastext labels.
 */
export function resolveLinks(sp: Subpage, rows: Row[]): Link[] {
  const explicit = sp.links.map((l) => ({ ...l, origin: l.origin ?? ('map' as const) }));
  const overlaps = (a: Link, b: Link) => a.row === b.row && a.colStart < b.colEnd && b.colStart < a.colEnd;
  const scanned = scanLinks(rows, 1, 23).filter((s) => !explicit.some((e) => overlaps(e, s)));
  const ft = rows[24] ? fastextLinks(rows[24], sp.fastext) : [];
  return [...explicit, ...scanned, ...ft];
}

export function linkAt(links: Link[], row: number, col: number): Link | undefined {
  return links.find((l) => l.row === row && col >= l.colStart && col < l.colEnd);
}
